import { useMemo, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Copy, X } from "lucide-react"
import type { SharedProps } from "@/types/inertia"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { MultiSelect } from "@/components/ui/select"
import { RolePill, ROLE_LABEL } from "./role-pill"
import { fmtRelativeTime } from "@/lib/format"
import { useCan } from "@/lib/authz"
import { cn } from "@/lib/cn"
import type { RoleKey } from "@/types/inertia"
import type { TeamMember, AgentPoolOption } from "@/types/team"

// Right-rail drawer for a selected team member. Mirrors the
// FeedbackDetailDrawer layout: avatar+name header, segmented info cards
// (description / source-identity / MFA / sessions / activity), action
// footer. Sections that don't have data yet (MFA factors, active sessions,
// activity feed) render placeholders so the chrome lands without blocking
// on the MFA workstream.

type Props = {
  member: TeamMember | null
  onClose: () => void
  agentPools: AgentPoolOption[]
}

function fmtAbsolute(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString()
}

export function TeamMemberDrawer({ member, onClose, agentPools }: Props) {
  if (!member) {
    return (
      <Card className="p-6 text-sm text-app-muted">
        Select a member on the left to see details.
      </Card>
    )
  }

  return (
    <Card className="space-y-5 p-5">
      <Header member={member} onClose={onClose} />
      <DescriptionBlock member={member} />
      <PoolsCard member={member} agentPools={agentPools} />
      <MfaCard member={member} />
      <ActionsFooter member={member} />
    </Card>
  )
}

// "Pools" section for the drawer. Renders the member's current pools as
// chips up top, then a multi-select that PATCHes /settings/team/:id with
// the new agent_pool_ids array. Server-side reconciliation lives in
// Portal::Team::SetMemberPools (cross-company ids silently filtered).
function PoolsCard({ member, agentPools }: { member: TeamMember; agentPools: AgentPoolOption[] }) {
  const [busy, setBusy] = useState(false)

  const poolsById = useMemo(() => {
    const map = new Map<number, AgentPoolOption>()
    for (const p of agentPools) map.set(p.id, p)
    return map
  }, [agentPools])

  const currentPools = useMemo(
    () => member.agent_pool_ids.map((id) => poolsById.get(id)).filter((p): p is AgentPoolOption => !!p),
    [member.agent_pool_ids, poolsById],
  )

  function onChange(nextIds: number[]) {
    if (busy) return
    // Skip server round-trip when nothing changed.
    const same = nextIds.length === member.agent_pool_ids.length
      && nextIds.every((id) => member.agent_pool_ids.includes(id))
    if (same) return
    setBusy(true)
    router.patch(`/settings/team/${member.id}`, { agent_pool_ids: nextIds }, {
      preserveScroll: true,
      onFinish: () => setBusy(false),
    })
  }

  return (
    <section className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-app-subtle">Pools</div>
      {currentPools.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {currentPools.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center rounded-chip border border-border bg-white/[0.05] px-2.5 py-0.5 text-xs text-app-text"
            >
              {p.name}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-xs text-app-muted">No pool memberships.</div>
      )}
      {agentPools.length > 0 && (
        <MultiSelect<number>
          value={currentPools.map((p) => ({ value: p.id, label: p.name }))}
          onChange={(opts) => onChange((opts ?? []).map((o) => o.value))}
          options={agentPools.map((p) => ({ value: p.id, label: p.name }))}
          placeholder="Add to pools…"
          isClearable
          isDisabled={busy}
        />
      )}
    </section>
  )
}

function Header({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <Avatar member={member} size={48} />
        <div>
          <h2 className="text-lg font-semibold leading-snug">{member.name}</h2>
          <div className="mt-1 flex items-center gap-2">
            <RolePill role={member.role} />
            <StatusDot status={member.status} label={member.status_label} />
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close detail"
        className="rounded-control p-1 text-app-muted hover:bg-white/5 hover:text-app-text"
      >
        <X size={18} strokeWidth={2} />
      </button>
    </header>
  )
}

function DescriptionBlock({ member }: { member: TeamMember }) {
  const [copied, setCopied] = useState(false)
  function copyEmail() {
    navigator.clipboard.writeText(member.email).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    })
  }
  return (
    <section className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-app-subtle">Description</div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-app-muted">Display name</dt>
        <dd className="text-app-text">{member.name}</dd>
        <dt className="text-app-muted">Email</dt>
        <dd className="flex items-center gap-1.5 text-app-text">
          <span className="break-all">{member.email}</span>
          <button
            type="button"
            onClick={copyEmail}
            aria-label="Copy email"
            className="rounded-control p-0.5 text-app-muted hover:bg-white/5 hover:text-app-text"
          >
            <Copy size={12} strokeWidth={2} />
          </button>
          {copied && <span className="text-[11px] text-status-success">Copied</span>}
        </dd>
        <dt className="text-app-muted">Member since</dt>
        <dd className="text-app-text">{fmtAbsolute(member.accepted_at ?? member.invited_at)}</dd>
        <dt className="text-app-muted">Last login</dt>
        <dd className="text-app-text">{fmtRelativeTime(member.last_login_at)}</dd>
      </dl>
    </section>
  )
}

function MfaCard({ member }: { member: TeamMember }) {
  const { props } = usePage<SharedProps>()
  const isMe = props.current_user?.id === member.user_id
  return (
    <section className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-app-subtle">MFA factors</div>
      <Card variant="inset" className="space-y-2 p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-app-muted">Status</span>
          {member.mfa_enrolled ? (
            <Badge tone="success">Enrolled</Badge>
          ) : isMe ? (
            <a
              href="/portal/mfa/enroll"
              className="rounded-chip border border-status-warning/40 bg-status-warning/10 px-2.5 py-0.5 text-xs font-medium text-status-warning hover:bg-status-warning/20"
            >
              Not enrolled — set up now
            </a>
          ) : (
            <Badge tone="warning">Not enrolled</Badge>
          )}
        </div>
      </Card>
    </section>
  )
}

function ActionsFooter({ member }: { member: TeamMember }) {
  const can = useCan()
  const [busy, setBusy] = useState(false)

  function postMember(action: "resend_invite" | "disable" | "enable") {
    if (busy) return
    setBusy(true)
    router.post(`/settings/team/${member.id}/${action}`, {}, {
      preserveScroll: true,
      onFinish: () => setBusy(false),
    })
  }

  function changeRole(newRole: RoleKey) {
    if (busy || newRole === member.role) return
    setBusy(true)
    router.patch(`/settings/team/${member.id}`, { role: newRole }, {
      preserveScroll: true,
      onFinish: () => setBusy(false),
    })
  }

  function remove() {
    if (busy) return
    if (!window.confirm(`Remove ${member.name} from this workspace?`)) return
    setBusy(true)
    router.delete(`/settings/team/${member.id}`, {
      preserveScroll: true,
      onFinish: () => setBusy(false),
    })
  }

  const roleOptions = member.demotable_to

  return (
    <footer className="space-y-3 border-t border-border pt-4">
      {member.can_change_role && roleOptions.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <label className="text-app-muted" htmlFor="role-select">Role</label>
          <select
            id="role-select"
            value={member.role}
            disabled={busy}
            onChange={(e) => changeRole(e.target.value as RoleKey)}
            className="rounded-control border border-border bg-white/5 px-2 py-1.5 text-sm text-app-text outline-none focus:border-brand-red/50"
          >
            <option value={member.role}>{ROLE_LABEL[member.role] ?? member.role}</option>
            {roleOptions.filter((r) => r !== member.role).map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {member.status === "pending" && can("team_invite") && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => postMember("resend_invite")}>
            Resend invite
          </Button>
        )}
        {member.status === "active" && can("team_remove") && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => postMember("disable")}>
            Disable
          </Button>
        )}
        {member.status === "disabled" && can("team_remove") && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => postMember("enable")}>
            Re-enable
          </Button>
        )}
        {member.can_remove && (
          <Button variant="red" size="sm" disabled={busy} onClick={remove}>
            Remove
          </Button>
        )}
      </div>
    </footer>
  )
}

function Avatar({ member, size }: { member: TeamMember; size: number }) {
  const px = `${size}px`
  const [imgFailed, setImgFailed] = useState(false)
  const initial = (member.name.trim()[0] ?? "?").toUpperCase()
  if (member.avatar_url && !imgFailed) {
    return (
      <img
        src={member.avatar_url}
        alt={member.name}
        onError={() => setImgFailed(true)}
        style={{ width: px, height: px }}
        className="shrink-0 rounded-control border border-border object-cover"
      />
    )
  }
  return (
    <div
      style={{ width: px, height: px }}
      className="flex shrink-0 items-center justify-center rounded-control bg-gradient-to-br from-brand-red to-brand-redDeep font-semibold text-white shadow-redGlow"
    >
      {initial}
    </div>
  )
}

function StatusDot({ status, label }: { status: TeamMember["status"]; label: string }) {
  const colorClass = cn(
    "inline-block h-2 w-2 rounded-full",
    status === "active"   && "bg-status-success",
    status === "pending"  && "bg-status-warning",
    status === "disabled" && "bg-status-error",
  )
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-app-muted">
      <span className={colorClass} aria-hidden /> {label}
    </span>
  )
}
